import { customListSubject, customSubjects, workSubject } from '@/app/permissions/subjects.ts'
import { AbilityBuilder, MongoAbility } from '@casl/ability'
import { z } from 'zod'

export type USER_ROLE = 'ADMIN' | 'USER' | 'SUBSCRIBED_USER'

export interface UserAbilityDetails {
  id: string
  trialQuoteLimit: number | null
  isTelegramSubscriber: boolean
  notionDatabaseId?: string | null
  role: USER_ROLE
  emailConfirmed: boolean
}

const appAbilitySchema = z.union([workSubject, customListSubject, customSubjects])

export type AppAbilities = z.infer<typeof appAbilitySchema>

export type AppAbility = MongoAbility<AppAbilities>

type PermissionsByRole = (user: UserAbilityDetails, builder: AbilityBuilder<AppAbility>) => void

export const permissions: Record<USER_ROLE, PermissionsByRole> = {
  ADMIN(user, { can, cannot }) {
    can('manage', 'all')
    can('show', 'subscriber-indicator')

    if (user.emailConfirmed) {
      cannot('show', 'email-not-confirmed-indicator')
    }

    if (!user.notionDatabaseId) {
      cannot('show', 'sync-notion-button')
    }

    if (user.isTelegramSubscriber) {
      cannot('show', 'telegram-button')
    }
  },
  SUBSCRIBED_USER(user, { can, cannot }) {
    can('show', 'subscriber-indicator')
    can('show', 'custom-list')
    can('show', 'scrapping-report-feature')
    can('manage', 'work', { userId: { $eq: user.id } })
    can('show', 'show-rsync-work-button-table')

    if (!user.emailConfirmed) {
      can('show', 'email-not-confirmed-indicator')
    }

    if (!user.isTelegramSubscriber) {
      can('show', 'telegram-button')
    }

    can(['edit', 'delete'], 'custom-list', {
      ownerId: { $eq: user.id },
    })

    if (!user.notionDatabaseId) {
      cannot('show', 'sync-notion-button')
    }
  },
  USER(user, { can }) {
    can('manage', 'work', { userId: { $eq: user.id } })

    if (user.trialQuoteLimit && user.trialQuoteLimit > 0) {
      can('create', 'work', { userId: { $eq: user.id } })
    }

    if (!user.emailConfirmed) {
      can('show', 'email-not-confirmed-indicator')
    }

    if (!user.isTelegramSubscriber) {
      can('show', 'telegram-button')
    }
  },
}
